// src/modules/DataManagement/Users.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileSelector from '../../components/FileSelector';
import { showErrorToast, showSuccessToast } from '../../components/Toast/actions';
import { FileExtension } from '../../types/file';
import { downloadCSVFile, exportCSV, importCSV } from '../../utils/fileHelper';
import { importUserConverter, exportUserConverter } from '../Users/converter';
import { useUsersHook } from '../Users/store'; // Zorg dat dit de geüpdatete hook met getTemplateForImport importeert
import { OptionButton, OptionButtonType } from '../../components/Button/OptionButton';
import { startCaseKeysObj } from '../../utils/object';

import { StorageKeys } from '../../types/storage';

const UserImportExport: React.FC = () => {
  const { t } = useTranslation();
  // eslint-disable-next-line no-empty-pattern
  const [{}, actions] = useUsersHook();
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  // Handler voor het selecteren van een bestand
  const handleFileSelected = async (file: File) => {
    setSelectedFile(file);
    try {
      const data = await importCSV(file);
      setPreviewData(data);
      setShowPreview(true);
    } catch (error) {
      console.error('Error reading CSV:', error);
      showErrorToast({
        title: t('user.import_user_toast.failed.title'),
        subtitle: t('user.import_user_toast.failed.subtitle')
      });
    }
  };

  // Handler voor het importeren van het bestand
  const handleImport = async () => {
    if (!selectedFile) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem(StorageKeys.TOKEN);
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch('https://api.test.entradasecurity.com/api/V2/import/users', {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: token
        }
      });

      if (response.status === 200) {
        showSuccessToast({
          title: t('user.import_user_toast.success.title'),
          subtitle: t('user.import_user_toast.success.subtitle')
        });
        setShowPreview(false);
        setSelectedFile(null);
        setPreviewData([]);
      } else {
        throw new Error('Import failed');
      }
    } catch (error) {
      console.error('Error importing users:', error);
      showErrorToast({
        title: t('user.import_user_toast.failed.title'),
        subtitle: t('user.import_user_toast.failed.subtitle')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handler voor het exporteren van gebruikersdata naar een CSV-bestand
  const exportUserList = async () => {
    setLoading(true);
    try {
      const exportUserInfoList = await actions.getExportUserInfoList();
      const startCaseDataList = exportUserInfoList.map(info => startCaseKeysObj(exportUserConverter(info)));
      const csvData = exportCSV(startCaseDataList);
      downloadCSVFile(csvData, 'users_export.csv');
      showSuccessToast({
        title: t('user.export_user_toast.success.title'),
        subtitle: t('user.export_user_toast.success.subtitle')
      });
    } catch {
      showErrorToast({
        title: t('user.export_user_toast.failed.title'),
        subtitle: t('user.export_user_toast.failed.subtitle')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handler voor het downloaden van de importtemplate
  const downloadTemplate = async () => {
    setLoading(true);
    try {
      const templateData = `userId;userMail;userFullName;userImage;userTagNumber;userLocationId;userComment;userRoomNumber;userTagActive;userPhone;userStartDate;userEndDate;userTagText;userFreeTextText1;userFreeTextText2;userEmergency;userVisitor;userPin;userSpecialRights;userDepartment;userEmployeeNr;groupIds
7804;d.smit@testimport.nl;Damian Smit;;A12B34C5;15;Developer;101;1;0612345678;2024-03-14 09:00:00;2025-03-14 09:00:00;Badge123;Schoenmaat 42;Kledingmaat L;1;0;1234;0;Development;EMP001;73,865,883
;a.smith@testimport.nl;Anna Smit;;B98C76D4;16;Manager;202;1;0687654321;2024-03-14 09:00:00;2025-03-14 09:00:00;Badge456;Schoenmaat 38;Kledingmaat M;0;0;5678;1;Management;EMP002;73,75`;

      downloadCSVFile(templateData, 'user_import_template.csv');
      showSuccessToast({
        title: t('user.download_template.success.title'),
        subtitle: t('user.download_template.success.subtitle')
      });
    } catch {
      showErrorToast({
        title: t('user.download_template.failed.title'),
        subtitle: t('user.download_template.failed.subtitle')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        width: '100vw',
        padding: 4,
        textAlign: 'center',
        backgroundColor: '#f4f4f9'
      }}>
      <Typography variant="h3" gutterBottom>
        {t('user.import_export.title')}
      </Typography>
      <Typography variant="body1" mb={4}>
        {t('user.import_export.description')}
      </Typography>

      <Box display="flex" gap={3} mb={4}>
        <OptionButton
          type={OptionButtonType.EXPORT}
          content={t('export')}
          onClick={exportUserList}
          isscaledown={true}
        />
        <OptionButton
          type={OptionButtonType.IMPORT}
          content={t('import')}
          onClick={() => inputFileRef.current?.click()}
          isscaledown={true}
        />
        <OptionButton
          type={OptionButtonType.DOWNLOAD}
          content={t('download_template')}
          onClick={downloadTemplate}
          isscaledown={true}
        />
      </Box>

      <FileSelector inputFileRef={inputFileRef} onFileSelected={handleFileSelected} fileExts={[FileExtension.CSV]} />

      {showPreview && previewData.length > 0 && (
        <Box mt={4} width="100%" maxWidth="1200px">
          <Typography variant="h5" gutterBottom>
            {t('user.import_preview.title')}
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {Object.keys(previewData[0]).map(header => (
                    <TableCell key={header}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.map((row, index) => (
                  <TableRow key={index}>
                    {Object.values(row).map((value: any, cellIndex) => (
                      <TableCell key={cellIndex}>{value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <Button variant="contained" color="primary" onClick={handleImport} disabled={loading}>
              {t('user.import_preview.confirm')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setShowPreview(false);
                setSelectedFile(null);
                setPreviewData([]);
              }}>
              {t('user.import_preview.cancel')}
            </Button>
          </Box>
        </Box>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress color="primary" />
        </Box>
      )}
    </Box>
  );
};

export default UserImportExport;
